(() => {
  if (customElements.get("basic-bundle-builder")) return;

  class BasicBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(
        this.querySelector("[data-bundle-variants]").textContent,
      );
      this.media = JSON.parse(
        this.querySelector("[data-bundle-media]")?.textContent || "[]",
      );
      this.bundleQuantity = Number(this.dataset.quantity);
      this.bundleTotal = Number(this.dataset.total);
      this.optionNames = this.dataset.optionNames.split("||");
      this.tier = "bundle";
      this.selectionRoot = this.querySelector("[data-selections]");
      this.addButton = this.querySelector("[data-add]");
      this.error = this.querySelector("[data-error]");
      this.sticky = this.querySelector("[data-bundle-sticky]");
      this.stickySummary = this.querySelector("[data-sticky-summary]");
      this.stickyButton = this.querySelector("[data-sticky-action]");
      this.money = new Intl.NumberFormat("ms-MY", {
        style: "currency",
        currency: this.dataset.currency || "MYR",
      });

      this.options = this.optionNames.map((name, index) => ({
        name,
        values: [
          ...new Set(this.variants.map((variant) => variant.options[index])),
        ],
      }));
      this.sizeIndex = this.options.findIndex((option) => /size|saiz/i.test(option.name));
      this.savedSelections = {};
      const linkedVariant = this.variants.find((variant) => String(variant.id) === new URLSearchParams(location.search).get('variant'));
      if (linkedVariant) this.savedSelections.bundle = [linkedVariant.options];

      this.querySelectorAll("[data-tier]").forEach((button) => {
        button.addEventListener("click", () =>
          this.chooseTier(button.dataset.tier),
        );
      });
      this.addButton.addEventListener("click", () => this.addToCart());
      this.renderColorPalette();
      this.renderSelections();
      this.updateSummary();
      this.setupSticky();
    }

    disconnectedCallback() {
      this.stickyObserver?.disconnect();
      this.sticky?.remove();
    }

    chooseTier(tier) {
      if (this.busy || tier === this.tier) return;
      this.savedSelections[this.tier] = Array.from({ length: this.quantity }, (_, index) => this.selectedOptionsForRow(index));
      if (!this.savedSelections[tier]) this.savedSelections[tier] = [this.savedSelections[this.tier][0]];
      this.tier = tier;
      this.querySelectorAll("[data-tier]").forEach((button) => {
        const selected = button.dataset.tier === tier;
        button.classList.toggle("is-selected", selected);
        button.setAttribute("aria-checked", String(selected));
      });
      this.renderSelections();
      this.updateSummary();
    }

    get quantity() {
      return this.tier === "bundle" ? this.bundleQuantity : 1;
    }

    get total() {
      return this.tier === "bundle" ? this.bundleTotal : this.rowPrice(0);
    }

    rowPrice(index) {
      const selected = this.selectedOptionsForRow(index);
      const variant = this.selectedVariantForRow(index) || this.variants.find((item) => item.options.every((value, i) => !selected[i] || value === selected[i]));
      return variant?.price ?? this.variants[0].price;
    }

    setupSticky() {
      if (
        !this.sticky ||
        !this.stickyButton ||
        !matchMedia("(max-width: 749px)").matches
      )
        return;
      if (this.dataset.themeRole !== "main") {
        this.sticky.classList.add("is-preview");
      }
      document.body.append(this.sticky);
      this.stickyButton.addEventListener("click", () => {
        const target = this.querySelector("[data-color-palette]") || this;
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          this.sticky.hidden = entry.isIntersecting;
        },
        { threshold: 0.25 },
      );
      this.stickyObserver.observe(this.addButton);
    }

    renderSelections() {
      const rows = Array.from({ length: this.quantity }, (_, rowIndex) => {
        const fields = this.options
          .map((option, optionIndex) => {
            const options = option.values
              .map(
                (value) =>
                  `<option value="${this.escape(value)}">${this.escape(value)}</option>`,
              )
              .join("");
            const colorAttribute = this.isColorOption(option.name)
              ? ' data-color-option="true"'
              : "";
            const placeholder = optionIndex === this.sizeIndex ? '<option value="">Pilih saiz</option>' : '';
            const label = optionIndex === this.sizeIndex ? 'Saiz' : this.isColorOption(option.name) ? 'Warna' : option.name;
            return `<label><span>${this.escape(label)}</span><select data-row="${rowIndex}" data-option="${optionIndex}"${colorAttribute} aria-label="${this.escape(label)} helai ${rowIndex + 1}">${placeholder}${options}</select></label>`;
          })
          .join("");
        const errorId = `ba-bundle-row-error-${this.dataset.productId}-${rowIndex}`;
        return `<div class="ba-bundle__row" data-selection-row="${rowIndex}"><b>#${rowIndex + 1}</b>${fields}<p class="ba-bundle__row-error" id="${errorId}" data-row-error="${rowIndex}" role="alert" hidden></p></div>`;
      }).join("");

      const sizes = this.options[this.sizeIndex];
      const applyAll = sizes && this.quantity > 1 ? `<div class="ba-bundle__same-size"><label><span>Saiz sama untuk semua</span><select data-size-all aria-label="Saiz sama untuk semua"><option value="">Pilih saiz untuk semua</option>${sizes.values.map((value) => `<option value="${this.escape(value)}">${this.escape(value)}</option>`).join('')}</select></label><small>Boleh ubah saiz setiap helai di bawah untuk campur saiz.</small></div>` : '';
      this.selectionRoot.innerHTML = `${applyAll}<div class="ba-bundle__selection-heading"><strong>Pilih setiap helai</strong><span data-completion></span></div>${rows}`;
      this.selectionRoot.querySelectorAll('select[data-row]').forEach((select) => {
        const saved = this.savedSelections[this.tier]?.[Number(select.dataset.row)]?.[Number(select.dataset.option)];
        if (saved !== undefined) select.value = saved;
      });
      this.selectionRoot.querySelector('[data-size-all]')?.addEventListener('change', (event) => {
        if (!event.target.value) return;
        this.selectionRoot.querySelectorAll(`select[data-option="${this.sizeIndex}"]`).forEach((select) => { select.value = event.target.value; });
        this.hideError();
        this.validateSelections();
        this.updateSummary();
      });
      this.selectionRoot
        .querySelectorAll("select[data-row]")
        .forEach((select) => {
          select.addEventListener("change", () => {
            if (select.matches("[data-color-option]")) {
              this.previewColor(select.value);
            }
            this.validateRow(Number(select.dataset.row));
            this.updateSummary();
            const remainingIssues = this.selectionIssues();
            if (!remainingIssues.length) {
              this.hideError();
            } else if (!this.error.hidden) {
              this.showSelectionIssues(remainingIssues);
            }
          });
        });
      this.hideError();
      this.updateSummary();
    }

    renderColorPalette() {
      const colorOptionIndex = this.options.findIndex((option) =>
        this.isColorOption(option.name),
      );
      const colorOption = this.options[colorOptionIndex];
      const palette = this.querySelector("[data-color-palette]");
      if (colorOptionIndex < 0 || !palette) return;
      const colors = colorOption.values
        .map((value) => {
          const colorMedia = this.colorMedia(value);
          if (!colorMedia?.src) return "";
          return `
        <button class="ba-bundle__palette-item" type="button" data-preview-color="${this.escape(value)}" title="Lihat ${this.escape(value)} pada gambar utama">
          <img src="${this.escape(colorMedia.src)}" alt="${this.escape(value)}" loading="lazy" width="72" height="72">
          <span>${this.escape(value)}</span>
        </button>`;
        })
        .join("");
      palette.innerHTML = `<strong>Lihat warna pada gambar utama</strong><div class="ba-bundle__palette-list">${colors}</div>`;
      palette.querySelectorAll("[data-preview-color]").forEach((button) => {
        button.addEventListener("click", () =>
          this.previewColor(button.dataset.previewColor, true),
        );
      });
      palette.hidden = false;
    }

    isColorOption(name) {
      return /colou?r|warna/i.test(name);
    }

    colorMedia(value) {
      const colorOptionIndex = this.options.findIndex((option) =>
        this.isColorOption(option.name),
      );
      const colorOption = this.options[colorOptionIndex];
      const variant = this.variants.find(
        (item) =>
          item.options[colorOptionIndex] === value &&
          (item.featured_media || item.featured_image),
      );
      if (variant) {
        return {
          id: variant.featured_media?.id,
          src:
            variant.featured_media?.preview_image?.src ||
            variant.featured_image?.src,
        };
      }

      const imageMedia = this.media.filter(
        (item) => item.media_type === "image" || item.preview_image,
      );
      const colorIndex = colorOption?.values.indexOf(value) ?? -1;
      const fallback = imageMedia.slice(-colorOption.values.length)[colorIndex];
      return fallback
        ? { id: fallback.id, src: fallback.preview_image?.src }
        : null;
    }

    previewColor(value, scrollToGallery = false) {
      const colorMedia = this.colorMedia(value);
      if (!colorMedia) return;
      this.querySelectorAll("[data-preview-color]").forEach((button) => {
        button.classList.toggle(
          "is-active",
          button.dataset.previewColor === value,
        );
      });

      const mediaId = colorMedia.id;
      const imageUrl = colorMedia.src;
      const gallery = document.querySelector("media-gallery");
      const media = mediaId
        ? gallery?.querySelector(`[data-media-id$="-${mediaId}"]`)
        : null;
      if (media && typeof gallery.setActiveMedia === "function") {
        gallery.setActiveMedia(media.dataset.mediaId, true, scrollToGallery);
      } else {
        const activeMedia =
          gallery?.querySelector(".product__media-item.is-active") ||
          gallery?.querySelector(".product__media-item");
        const image = activeMedia?.querySelector("img");
        if (image && imageUrl) {
          image.removeAttribute("srcset");
          image.src = imageUrl;
          image.alt = value;
        }
      }

      if (scrollToGallery)
        gallery?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    selectedOptionsForRow(rowIndex) {
      return this.options.map(
        (_, optionIndex) =>
          this.querySelector(
            `select[data-row="${rowIndex}"][data-option="${optionIndex}"]`,
          ).value,
      );
    }

    selectedVariantForRow(rowIndex) {
      const selected = this.selectedOptionsForRow(rowIndex);
      return this.variants.find(
        (variant) =>
          variant.options.every((value, index) => value === selected[index]),
      );
    }

    selectionIssueForRow(rowIndex) {
      const selected = this.selectedOptionsForRow(rowIndex);
      const variant = this.selectedVariantForRow(rowIndex);
      if (variant?.available) return null;
      return { rowIndex, selected, variant, incomplete: selected.some((value) => !value) };
    }

    selectionIssues() {
      return Array.from({ length: this.quantity }, (_, rowIndex) =>
        this.selectionIssueForRow(rowIndex),
      ).filter(Boolean);
    }

    showSelectionIssues(issues, { scrollToFirst = false } = {}) {
      const details = issues
        .map(
          (issue) =>
            `Helai #${issue.rowIndex + 1} — ${issue.selected.join(" / ")}`,
        )
        .join("; ");
      this.showError(
        issues.some((issue) => issue.incomplete)
          ? 'Sila pilih saiz untuk setiap helai sebelum menambah ke troli.'
          : `Stok habis untuk ${details}. Sila tukar pilihan yang ditanda merah.`,
      );
      if (scrollToFirst) {
        this.querySelector(
          `[data-selection-row="${issues[0].rowIndex}"]`,
        )?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        this.querySelector(`[data-selection-row="${issues[0].rowIndex}"] select[aria-invalid="true"]`)?.focus({ preventScroll: true });
      }
    }

    validateRow(rowIndex) {
      const issue = this.selectionIssueForRow(rowIndex);
      const row = this.querySelector(`[data-selection-row="${rowIndex}"]`);
      const rowError = this.querySelector(`[data-row-error="${rowIndex}"]`);
      if (!row || !rowError) return issue;

      row.classList.toggle("is-unavailable", Boolean(issue));
      row.querySelectorAll("select").forEach((select) => {
        if (issue) {
          select.setAttribute("aria-invalid", "true");
          select.setAttribute("aria-describedby", rowError.id);
        } else {
          select.removeAttribute("aria-invalid");
          select.removeAttribute("aria-describedby");
        }
      });

      if (issue) {
        rowError.textContent = issue.incomplete ? `Helai #${rowIndex + 1}: sila pilih saiz.` : `Helai #${rowIndex + 1}: ${issue.selected.join(" / ")} kehabisan stok.`;
        rowError.hidden = false;
      } else {
        rowError.textContent = "";
        rowError.hidden = true;
      }
      return issue;
    }

    validateSelections({ showGlobal = false } = {}) {
      const issues = Array.from({ length: this.quantity }, (_, rowIndex) =>
        this.validateRow(rowIndex),
      ).filter(Boolean);
      if (showGlobal && issues.length) {
        this.showSelectionIssues(issues, { scrollToFirst: true });
      }
      return issues;
    }

    async addToCart() {
      if (this.busy) return;
      const issues = this.validateSelections({ showGlobal: true });
      if (issues.length) return;

      const selectedVariants = Array.from(
        { length: this.quantity },
        (_, index) => this.selectedVariantForRow(index),
      );

      const bundleId =
        globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      const grouped = new Map();
      selectedVariants.forEach((variant) =>
        grouped.set(variant.id, (grouped.get(variant.id) || 0) + 1),
      );
      const items = [...grouped].map(([id, quantity]) => ({
        id,
        quantity,
        properties: {
          _basic_bundle_id: bundleId,
          Pakej:
            this.tier === "bundle"
              ? `${this.bundleQuantity} Helai RM${this.bundleTotal / 100}`
              : "Beli 1",
        },
      }));

      this.busy = true;
      this.querySelectorAll('select, [data-tier]').forEach((control) => { control.disabled = true; });
      this.addButton.disabled = true;
      this.addButton.textContent = "Menambah ke troli…";
      this.hideError();

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ items }),
        });
        if (!response.ok)
          throw new Error(
            (await response.json()).description ||
              "Tidak dapat menambah ke troli.",
          );
        this.addButton.textContent = "Sudah ditambah ✓";
        document.dispatchEvent(
          new CustomEvent("basic-bundle:added", {
            detail: { items, bundleId },
          }),
        );
        window.setTimeout(
          () => window.location.assign(this.dataset.cartUrl),
          350,
        );
      } catch (error) {
        this.busy = false;
        this.querySelectorAll('select, [data-tier]').forEach((control) => { control.disabled = false; });
        this.showError(
          error.message || "Tidak dapat menambah ke troli. Sila cuba lagi.",
        );
        this.addButton.disabled = false;
        this.updateSummary();
      }
    }

    updateSummary() {
      const format = (cents) => this.money.format(cents / 100);
      const issues = this.selectionIssues();
      this.querySelector('[data-completion]').textContent = `${this.quantity - issues.length}/${this.quantity} lengkap`;
      const allSizes = Array.from({ length: this.quantity }, (_, index) => this.selectedOptionsForRow(index)[this.sizeIndex]);
      const sameSize = this.querySelector('[data-size-all]');
      if (sameSize) sameSize.value = allSizes.every((size) => size === allSizes[0]) ? allSizes[0] || '' : '';
      const regularTotal = Array.from({ length: this.quantity }, (_, index) => this.rowPrice(index)).reduce((sum, price) => sum + price, 0);
      const saving = this.tier === 'bundle' ? Math.max(0, regularTotal - this.total) : 0;
      const savingLabel = this.querySelector('[data-saving]');
      savingLabel.hidden = saving <= 0;
      savingLabel.textContent = saving > 0 ? `Anda jimat ${format(saving)} berbanding beli satu-satu` : '';
      this.querySelector('[data-single-price]').textContent = format(this.rowPrice(0));
      const priceRoot = this.closest('product-info')?.querySelector('[data-order-price]');
      if (priceRoot) {
        priceRoot.querySelector('[data-order-total]').textContent = format(this.total);
        priceRoot.querySelector('[data-order-label]').textContent = this.tier === 'bundle' ? `Set ${this.quantity} helai · ${format(this.total / this.quantity)}/helai` : '1 helai';
        priceRoot.querySelector('[data-order-regular]').textContent = saving > 0 ? format(regularTotal) : '';
        priceRoot.querySelector('[data-order-regular]').hidden = saving <= 0;
        priceRoot.querySelector('[data-order-saving]').textContent = saving > 0 ? `Jimat ${format(saving)} berbanding beli satu-satu` : '';
        priceRoot.querySelector('[data-order-saving]').hidden = saving <= 0;
      }
      this.querySelector("[data-summary-units]").textContent =
        `${this.quantity} helai`;
      this.querySelector("[data-summary-total]").textContent =
        `Jumlah ${this.money.format(this.total / 100)}`;
      this.addButton.textContent = `Tambah ${this.quantity} helai — ${this.money.format(this.total / 100)}`;
      this.addButton.disabled = Boolean(this.busy);
      this.dataset.selectionSummary = `${this.quantity} Helai · ${format(this.total)}`;
      this.dispatchEvent(new CustomEvent('basic-bundle:selection', { bubbles: true, detail: { summary: this.dataset.selectionSummary } }));
      if (this.stickySummary) {
        this.stickySummary.textContent = `${this.quantity} Helai · ${this.money.format(this.total / 100)}`;
      }
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }
    hideError() {
      this.error.hidden = true;
      this.error.textContent = "";
    }
    escape(value) {
      const node = document.createElement("span");
      node.textContent = value;
      return node.innerHTML;
    }
  }

  customElements.define("basic-bundle-builder", BasicBundleBuilder);
})();
